import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@halo/web-lib';

export type Severity = 'critical' | 'warning' | 'info';
export type Status = 'open' | 'acknowledged' | 'ignored' | 'all';
export type ResolveAction = 'acknowledge' | 'ignore' | 'reopen';

export interface AnomalyRow {
  storeId: number;
  storeName: string;
  platform: string;
  metric: string;
  date: string;
  detectedAt: string;
  severity: Severity;
  rule: string;
  actualValue: number;
  expectedValue: number;
  deviation: number;
  baselineWindow: number;
  streakLength: number;
  status: 'open' | 'acknowledged' | 'ignored';
  resolvedBy: string;
  resolvedAt: string | null;
  resolvedNote: string;
}

export interface AnomaliesQuery {
  from: string;
  to: string;
  storeId?: number;
  platforms?: string[];
  severities?: Severity[];
  status?: Status;
  rule?: string;
  limit?: number;
  offset?: number;
}

export interface AnomaliesResponse {
  anomalies: AnomalyRow[];
  summary: { critical: number; warning: number; info: number; total: number };
  pagination: { limit: number; offset: number; total: number };
}

function buildQuery(q: AnomaliesQuery): string {
  const sp = new URLSearchParams();
  // Backend expects `start`/`end` (codebase convention shared with the
  // generic /:viewName routes); we keep `from`/`to` in the hook interface
  // because the FilterBar binds <input type="date"> values to those names.
  sp.set('start', q.from);
  sp.set('end', q.to);
  if (q.storeId !== undefined) sp.set('storeId', String(q.storeId));
  for (const p of q.platforms ?? []) sp.append('platform', p);
  for (const s of q.severities ?? []) sp.append('severity', s);
  if (q.status) sp.set('status', q.status);
  if (q.rule) sp.set('rule', q.rule);
  if (q.limit !== undefined) sp.set('limit', String(q.limit));
  if (q.offset !== undefined) sp.set('offset', String(q.offset));
  return sp.toString();
}

export function useAnomalies(query: AnomaliesQuery) {
  return useQuery({
    queryKey: ['anomalies', query],
    queryFn: async () => {
      const res = await api.get<AnomaliesResponse>(
        `/api/v1/analytics/anomalies?${buildQuery(query)}`,
      );
      if (!res.success || !res.data) throw new Error('Failed to load anomalies');
      return res.data;
    },
    refetchInterval: 60_000,
  });
}

export interface ResolveInput {
  anomalyKeys: Array<Pick<AnomalyRow, 'storeId' | 'platform' | 'metric' | 'date'>>;
  action: ResolveAction;
  note?: string;
}

export function useResolveAnomalies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: ResolveInput) => {
      const res = await api.post<{ updated: number }>('/api/v1/analytics/anomalies/resolve', input);
      if (!res.success) throw new Error('Resolve failed');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });
}

// Either `date` (single day) OR `from`+`to` (range, both inclusive, max 90 days). Neither = yesterday.
export type DateScope = { date?: string; from?: string; to?: string };

export type RescanInput =
  | ({ mode: 'single'; storeId: number } & DateScope)
  | ({ mode: 'list'; storeIds: number[] } & DateScope)
  | ({ mode: 'all' } & DateScope);

export interface RescanResult {
  enqueuedCount: number;
  jobIds: string[];
  days?: number;
  stores?: number;
}

export function useRescanAnomalies() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: RescanInput) => {
      const res = await api.post<RescanResult>('/api/v1/analytics/anomalies/rescan', input);
      if (!res.success) throw new Error('Rescan failed');
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['anomalies'] });
    },
  });
}

export interface BackfillInput {
  storeId: number;
  platform: string;
  date: string; // YYYY-MM-DD
}

export interface BackfillResult {
  jobIds: string[];
}

export function useBackfillDay() {
  return useMutation({
    mutationFn: async (input: BackfillInput) => {
      const res = await api.post<BackfillResult>('/api/v1/ingestion/admin/backfill', {
        storeId: input.storeId,
        platform: input.platform,
        dates: [input.date],
        force: true,
        triggerSource: 'admin',
      });
      if (!res.success) throw new Error('Backfill enqueue failed');
      return res.data;
    },
  });
}

export interface StoreSummary {
  id: number;
  name: string;
}

export function useAdminStores() {
  return useQuery({
    queryKey: ['admin-stores'],
    queryFn: async () => {
      const res = await api.get<StoreSummary[]>('/api/v1/admin/stores');
      if (!res.success || !res.data) throw new Error('Failed to load stores');
      return res.data.map((s) => ({ id: s.id, name: s.name }));
    },
    staleTime: 5 * 60_000,
  });
}
